import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ApprovalBanner from '../ApprovalBanner';
import { logout } from '../../redux/authSlice';
import { AppDispatch, RootState } from '../../redux/store';
import { Menu, LogOut, Bell, User } from 'lucide-react';

interface DashboardLayoutProps {
  sidebar: React.ReactNode;
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ sidebar, children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
  };

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const sidebarElement = React.isValidElement(sidebar)
    ? React.cloneElement(sidebar as React.ReactElement<any>, {
        isOpen: isSidebarOpen,
        onNavigate: () => setIsSidebarOpen(false),
      })
    : sidebar;

  // Admin pages are already wrapped by AdminLayout. Avoid nested header/sidebar duplication.
  if (isAdminRoute) {
    return <>{children}</>;
  }

  // Get page title from pathname
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/home')) return 'Dashboard';
    if (path.includes('/profile')) return 'Profile Management';
    if (path.includes('/documents')) return 'Document Upload';
    if (path.includes('/funding')) return 'Fund Account';
    if (path.includes('/payments') && !path.includes('/my-payments')) return 'New Payment';
    if (path.includes('/my-payments')) return 'My Payments';
    if (path.includes('/payment-details')) return 'Payment Details';
    if (path.includes('/change-password')) return 'Security Settings';
    return 'Dashboard';
  };

  const getPageSubtitle = () => {
    const path = location.pathname;
    if (path.includes('/home')) return 'Overview of your account activity';
    if (path.includes('/profile')) return 'Manage your personal information';
    if (path.includes('/documents')) return 'Upload and manage KYC documents';
    if (path.includes('/funding')) return 'Add funds to your account';
    if (path.includes('/payments') && !path.includes('/my-payments')) return 'Submit a new payment request';
    if (path.includes('/my-payments')) return 'Track all your payment requests';
    if (path.includes('/change-password')) return 'Update your security credentials';
    return '';
  };

  return (
    <div className="enterprise-dashboard-shell enterprise-dashboard-shell--user flex h-screen overflow-hidden">
      {/* Sidebar */}
      {sidebarElement}

      {/* Main Content Area */}
      <div className="enterprise-dashboard-main flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="enterprise-topbar flex-shrink-0 z-10">
          <div className="flex items-center justify-between px-4 sm:px-6 h-14">
            {/* Left: Mobile menu + Page title */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="lg:hidden p-2 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors"
                onClick={() => setIsSidebarOpen((prev) => !prev)}
                aria-label="Toggle sidebar menu"
              >
                <Menu size={20} />
              </button>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-base font-semibold text-fintech-text leading-tight truncate max-w-[140px] xs:max-w-[180px] sm:max-w-none">
                  {getPageTitle()}
                </h1>
                {getPageSubtitle() && (
                  <p className="text-xs text-fintech-textMuted hidden sm:block leading-tight mt-0.5">
                    {getPageSubtitle()}
                  </p>
                )}
              </div>
            </div>

            {/* Right: User actions */}
            <div className="flex items-center gap-2">
              {/* Date display */}
              <span className="hidden lg:block text-xs text-fintech-textMuted mr-2">
                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>

              {/* Notification bell */}
              <button className="p-2 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors relative" aria-label="Notifications">
                <Bell size={18} />
              </button>

              {/* User menu */}
              <div className="flex items-center gap-2 pl-2 border-l border-fintech-border">
                <div className="w-8 h-8 rounded-full bg-fintech-primary flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-white" />
                </div>
                <div className="hidden xs:block sm:block">
                  <p className="text-sm font-semibold text-fintech-text leading-tight truncate max-w-[80px] sm:max-w-none">{user?.name || 'User'}</p>
                  <p className="text-xs text-fintech-textMuted capitalize leading-tight">{user?.role || 'customer'}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-1 p-1.5 rounded-lg text-fintech-textMuted hover:text-fintech-error hover:bg-red-50 transition-colors"
                  title="Log out"
                  aria-label="Log out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Approval Banner */}
        <ApprovalBanner />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="enterprise-content p-4 sm:p-6 xl:p-8 2xl:p-10">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
